/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { CLIENT_EMIT_USER_CURRENT_STATUS, CLIENT_EMIT_USER_PROFILE, RECONNECT, RECONNECT_ATTEMPT, RECONNECT_FAILED } from 'services/socket-connection/events';
import { useLocalProfile, useStatusSnapshot } from 'shared-state/identity/hooks';
import { notifyActivityRecorded } from 'features/InactivityRules/activitySignal';
import {
    consumeReloadWithoutActivity,
    isPromptVisibleInThisTab,
    markReloadWithoutActivity,
    writeLastActivity
} from 'features/InactivityRules/inactivityStorage';
import { useSocket } from '../hooks';
import { SocketContextProvider, defaultSocketContextState, socketReducer } from './SocketContext';
import { useBoolean } from '@uifabric/react-hooks';
import SessionGate from 'features/SessionTakeover/SessionGate';
import { emitCallState, isLocalCallActiveNow, shouldAutoReloadOnOnline } from 'features/SessionTakeover/sessionClaim';
import { useSessionClaim } from 'features/SessionTakeover/useSessionClaim';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ISocketProviderProps {
    children?: React.ReactNode;
}

const LOCAL_STORAGE_KEYS = {
    ACCESS_TOKEN: 'ivAccessToken',
    LAST_AREA_ID: 'lastAreaId',
    LOCAL_ID: 'localID'
};

const SocketProvider: React.FunctionComponent<ISocketProviderProps> = ({ children }) => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    const lastAreaId = localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_AREA_ID);

    const webSocketBase = process.env.REACT_APP_WS_BASE;

    if (!token || !webSocketBase) {
        console.error('WebSocket connection cannot be established. Missing token or WebSocket base URL.');
        return null;
    }

    const socket = useSocket(webSocketBase, {
        extraHeaders: {
            Authorization: token
        },
        reconnectionAttempts: (window as any).Cypress ? 1 : 10,
        reconnectionDelay: 1000,
        autoConnect: false
    });
    const [SocketState, SocketDispatch] = useReducer(socketReducer, defaultSocketContextState);
    const localProfile = useLocalProfile();
    const statusSnapshot = useStatusSnapshot();
    // const [displayAlert, { toggle: toggleAlert }] = useBoolean(false);

    const session = useSessionClaim(socket);
    const { sessionStateRef, grantCount, replayHeldEmits } = session;

    const sendUserInfoToSocketIO = useCallback(() => {
        if (!localProfile || !socket || sessionStateRef.current !== 'active') return;
        if (!localProfile.room?.includes('undefined')) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.LOCAL_ID, localProfile.uid);
        }
        socket.emit(CLIENT_EMIT_USER_PROFILE, { localProfile, lastAreaId });
    }, [localProfile, socket, sessionStateRef]);

    const sendUserStatusToSocketIO = useCallback(() => {
        if (!socket || !statusSnapshot || sessionStateRef.current !== 'active') return;
        socket.emit(CLIENT_EMIT_USER_CURRENT_STATUS, { data: statusSnapshot });
    }, [socket, statusSnapshot, sessionStateRef]);

    const sendRef = useRef({ sendUserInfoToSocketIO, sendUserStatusToSocketIO });
    sendRef.current = { sendUserInfoToSocketIO, sendUserStatusToSocketIO };

    // After every grant: children's effects (SocketAdapter's handlers) already ran in this commit, so replies aren't lost.
    // Profile first, so the server knows the user again before the emits held while disconnected.
    useEffect(() => {
        if (grantCount === 0) return;
        sendRef.current.sendUserInfoToSocketIO();
        sendRef.current.sendUserStatusToSocketIO();
        if (socket && sessionStateRef.current === 'active') {
            emitCallState(socket, isLocalCallActiveNow());
            replayHeldEmits();
        }
    }, [grantCount, socket, replayHeldEmits]);

    const startListeners = useCallback(() => {
        socket.io.on(RECONNECT, (attempt: any) => {
            // Profile and status are re-sent once the server grants the resume claim (see useSessionClaim).
            console.info(`Reconnected on attempt ${attempt}`);
        });
        socket.io.on(RECONNECT_ATTEMPT, (attempt: any) => console.warn(`Reconnection attempt ${attempt}`));
        socket.io.on(RECONNECT_FAILED, () => {
            console.error('Reconnection failure');
            // toggleAlert();
        });

        return () => {
            socket.io.off(RECONNECT);
            socket.io.off(RECONNECT_ATTEMPT);
            socket.io.off(RECONNECT_FAILED);
        };
    }, [socket]);

    useEffect(() => {
        // connect to the websocket
        socket.connect();

        // save the socket in the context
        SocketDispatch({ type: 'UPDATE_SOCKET', payload: socket });

        // listen default connection event
        const cleanupListeners = startListeners();

        return () => {
            cleanupListeners();

            if (socket.connected) {
                socket.disconnect();
            }
        };
    }, [socket, startListeners]);

    useEffect(() => {
        localProfile && sendUserInfoToSocketIO();
        statusSnapshot && sendUserStatusToSocketIO();
    }, [localProfile, sendUserInfoToSocketIO, statusSnapshot, sendUserStatusToSocketIO]);

    /*
    !commented out for now- but keeping it here for future reference
    */
    // useEffect(() => {
    //     const handleVisibilityChange = () => {
    //         if (document.visibilityState === 'hidden') {
    //             console.warn('user changed tab');
    //             socket.emit('client-emit-user-status', { status: 'tab-hidden' });
    //         } else {
    //             console.warn('user back to tab');
    //             socket.emit('client-emit-user-status', { status: 'tab-visible' });
    //         }
    //     };

    //     const handleOnlineStatus = () => {
    //         console.warn('user is online');
    //         socket.emit('client-emit-user-status', { status: 'online' });

    //         // hard reload the page
    //         window.location.replace(window.location.href);
    //     };

    //     document.addEventListener('visibilitychange', handleVisibilityChange);
    //     window.addEventListener('online', handleOnlineStatus);

    //     return () => {
    //         document.removeEventListener('visibilitychange', handleVisibilityChange);
    //         window.removeEventListener('online', handleOnlineStatus);
    //     };
    // }, [socket]);

    useEffect(() => {
        const ACTIVITY_THROTTLE_MS = 5000;
        let lastWriteAt = 0;

        // Inactivity rules (useInactivityRules) read this shared timestamp; this effect only records activity.
        const recordActivity = (event: Event | null = null) => {
            if (event && !event.isTrusted) return; // prevent event from being triggered by the browser
            if (isPromptVisibleInThisTab()) return; // while "Are you still there?" is shown in this tab only its button counts as presence
            const now = Date.now();
            if (now - lastWriteAt < ACTIVITY_THROTTLE_MS) return;
            lastWriteAt = now;
            writeLastActivity(now);
            notifyActivityRecorded();
        };

        const handleOnlineStatus = () => {
            // A reload would claim the session again; a tab that lost it must not take it back on its own.
            if (!shouldAutoReloadOnOnline(sessionStateRef.current)) return;
            console.warn('user is online');
            markReloadWithoutActivity();
            // The reloaded page resumes with this tab's session id (useSessionClaim marks it on pagehide), so a tab replaced
            // while offline without hearing about it can't take the session back.
            socket.emit('client-emit-user-status', { status: 'online' });
            window.location.replace(window.location.href);
        };

        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

        activityEvents.forEach((event) => {
            document.addEventListener(event, recordActivity, { passive: true });
        });

        window.addEventListener('online', handleOnlineStatus);

        // Loading the page counts as activity, except the automatic reload after reconnecting
        if (!consumeReloadWithoutActivity()) recordActivity();

        return () => {
            activityEvents.forEach((event) => {
                document.removeEventListener(event, recordActivity);
            });

            window.removeEventListener('online', handleOnlineStatus);
        };
    }, [socket, sessionStateRef]);

    return (
        <SocketContextProvider value={{ SocketState, SocketDispatch }}>
            {/* disable it for now
            {displayAlert && <AlertModal title={IvicosStrings.generalAlertTitle} subText={IvicosStrings.webSocketAlert} toggleAlert={toggleAlert} />} */}
            <SessionGate session={session}>{children}</SessionGate>
        </SocketContextProvider>
    );
};

export default SocketProvider;
